import { createCompanyContextRequest, refreshSessionRequest } from './authApi';
import {
  clearCompanySession,
  getPlatformAuthToken,
  getSelectedCompanyId,
  getSelectedCompanyName,
  setPlatformAuthToken,
  setSelectedCompany,
} from './companySession';
import { getCompanyIdFromToken, isAccessTokenExpired } from '../casl/jwtUtils';

const ACCESS_TOKEN_KEY = 'authToken';

export const COMPANY_CONTEXT_LOST = 'COMPANY_CONTEXT_LOST';

let refreshPromise = null;
let authSessionListener = null;

export const subscribeAuthSession = (listener) => {
  authSessionListener = listener;
  return () => {
    if (authSessionListener === listener) {
      authSessionListener = null;
    }
  };
};

const notifyAuthSession = (token) => {
  authSessionListener?.(token);
};

const createCompanyContextLostError = () => {
  const error = new Error('Company context could not be restored');
  error.code = COMPANY_CONTEXT_LOST;
  return error;
};

export const getStoredAccessToken = () => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!token || token === 'undefined' || token === 'null') {
    return null;
  }
  return token;
};

export const setStoredAccessToken = (token) => {
  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }
};

export const clearStoredAccessToken = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  clearCompanySession();
};

const TOKEN_FIELD_NAMES = [
  'accesstoken',
  'access_token',
  'accesstokenjwt',
  'token',
  'jwt',
];

const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/;

const unwrapJsonValue = (data) => {
  let current = data;

  for (let i = 0; i < 5; i += 1) {
    if (typeof current !== 'string') {
      return current;
    }

    let text = current.replace(/^\uFEFF/, '').trim();
    if (text.startsWith(")]}'")) {
      text = text.replace(/^\)\]\}'?,?\s*/, '');
    }
    if (!text) {
      return text;
    }

    const first = text[0];
    if (first === '{' || first === '[' || first === '"') {
      try {
        current = JSON.parse(text);
        continue;
      } catch {
        return text;
      }
    }

    return text;
  }

  return current;
};

const looksLikeHtml = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim().slice(0, 32).toLowerCase();
  return (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<head') ||
    trimmed.startsWith('<body')
  );
};

const normalizeJwt = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  let token = value.replace(/^\uFEFF/, '').trim().replace(/^Bearer\s+/i, '').trim();
  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    token = token.slice(1, -1).trim();
  }

  token = token.replace(/\s/g, '');
  if (!token) {
    return null;
  }

  const embedded = token.match(JWT_RE);
  if (embedded) {
    return embedded[0];
  }

  return token.split('.').length === 3 ? token : null;
};

const readPropertyIgnoreCase = (object, name) => {
  if (!object || typeof object !== 'object' || Array.isArray(object)) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(object, name) && object[name] != null) {
    return object[name];
  }

  const target = name.toLowerCase();
  const key = Object.keys(object).find((candidate) => candidate.toLowerCase() === target);
  return key != null ? object[key] : undefined;
};

const findJwtInValue = (value, depth = 0) => {
  if (value == null || depth > 5) {
    return null;
  }

  const parsed = unwrapJsonValue(value);

  if (typeof parsed === 'string') {
    if (looksLikeHtml(parsed)) {
      return null;
    }
    return normalizeJwt(parsed);
  }

  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const found = findJwtInValue(item, depth + 1);
      if (found) {
        return found;
      }
    }
    return null;
  }

  if (typeof parsed !== 'object') {
    return null;
  }

  for (const key of TOKEN_FIELD_NAMES) {
    const raw = readPropertyIgnoreCase(parsed, key);
    const token =
      normalizeJwt(raw) ??
      (typeof raw === 'string' && raw.trim() && !looksLikeHtml(raw) ? raw.trim() : null);
    if (token) {
      return token;
    }
  }

  for (const [key, nested] of Object.entries(parsed)) {
    if (/token|jwt/i.test(key) || /data|result|payload|body|response|company/i.test(key)) {
      const found = findJwtInValue(nested, depth + 1);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

export const parseAccessTokenFromResponse = (data) => findJwtInValue(data);

const parseAccessTokenFromHeaders = (headers) => {
  if (!headers || typeof headers !== 'object') {
    return null;
  }

  return findJwtInValue(
    headers['x-access-token'] ??
      headers['x-auth-token'] ??
      headers.authorization ??
      headers.Authorization
  );
};

const tokenMatchesCompany = (token, expectedCompanyId) => {
  if (!token) {
    return false;
  }

  const expectedId = Number(expectedCompanyId);
  if (!Number.isInteger(expectedId) || expectedId <= 0) {
    return false;
  }

  return getCompanyIdFromToken(token) === expectedId;
};

const describeContextFailure = (raw, expectedCompanyId, foundToken) => {
  const expectedId = Number(expectedCompanyId);
  if (looksLikeHtml(raw)) {
    return 'Server je vratio HTML umjesto JSON tokena za /companies/:id/context';
  }

  if (foundToken) {
    const actualId = getCompanyIdFromToken(foundToken);
    return `token companyId=${actualId ?? 'nije pročitan'}, očekivano=${expectedId}`;
  }

  const unwrapped = unwrapJsonValue(raw);
  if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
    return `polja: ${Object.keys(unwrapped).join(', ') || 'objekat'}`;
  }

  if (typeof raw === 'string') {
    if (/eyJ/.test(raw)) {
      return 'string sadrži JWT koji nije prihvaćen';
    }
    const preview = raw.replace(/\s+/g, ' ').trim().slice(0, 48);
    return preview ? `string: ${preview}` : 'prazan string';
  }

  return raw == null ? 'prazan' : typeof raw;
};

/**
 * Use only a JWT whose companyId matches the selected company.
 * Never keep the supervisor token (companyId 0) after entering a board.
 */
export const resolveAccessTokenAfterCompanyContext = (
  response,
  expectedCompanyId
) => {
  const parsed = unwrapJsonValue(response?.data);
  const candidates = [
    normalizeJwt(parsed?.accessToken),
    normalizeJwt(parsed?.access_token),
    parseAccessTokenFromResponse(parsed),
    parseAccessTokenFromHeaders(response?.headers),
  ].filter(Boolean);

  for (const token of candidates) {
    if (tokenMatchesCompany(token, expectedCompanyId)) {
      return token;
    }
  }

  throw new Error(
    `Server nije vratio token za izabranu kompaniju (${describeContextFailure(
      parsed ?? response?.data,
      expectedCompanyId,
      candidates[0]
    )})`
  );
};

export const getCompanyFromContextResponse = (data) => {
  const parsed = unwrapJsonValue(data);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  return parsed.company ?? parsed.data?.company ?? parsed.result?.company ?? null;
};

export const getSupervisorAccessToken = () => {
  const currentToken = getStoredAccessToken();
  if (getCompanyIdFromToken(currentToken) === 0) {
    return currentToken;
  }

  const platformToken = getPlatformAuthToken();
  if (getCompanyIdFromToken(platformToken) === 0) {
    return platformToken;
  }

  return null;
};

/** Refresh the platform token when it is missing or expired. */
export const ensureSupervisorAccessToken = async () => {
  const current = getSupervisorAccessToken();
  if (current && !isAccessTokenExpired(current)) {
    return current;
  }

  const refreshed = await refreshAccessToken();
  if (getCompanyIdFromToken(refreshed) === 0) {
    setPlatformAuthToken(refreshed);
    return refreshed;
  }

  return getSupervisorAccessToken();
};

/** @deprecated Use getStoredAccessToken — kept for CASL helpers that accept an explicit token. */
export const getAuthToken = (explicitToken) => {
  if (explicitToken) {
    return explicitToken;
  }
  return getStoredAccessToken();
};

const applyCompanyContextToken = async (platformToken, companyId) => {
  const id = Number(companyId);
  const supervisorToken =
    getCompanyIdFromToken(platformToken) === 0
      ? platformToken
      : getSupervisorAccessToken();

  if (!supervisorToken || !Number.isInteger(id) || id <= 0) {
    throw new Error('Nedostaje token supervizora za ulazak u kompaniju');
  }

  const response = await createCompanyContextRequest(id, supervisorToken);
  const contextToken = resolveAccessTokenAfterCompanyContext(response, id);

  const company = getCompanyFromContextResponse(response.data);
  setPlatformAuthToken(supervisorToken);
  setSelectedCompany(id, company?.name ?? getSelectedCompanyName());
  setStoredAccessToken(contextToken);
  return contextToken;
};

export const ensureCompanyContextToken = async (token) => {
  const selectedCompanyId = getSelectedCompanyId();
  if (!token || selectedCompanyId == null) {
    return token;
  }

  if (getCompanyIdFromToken(token) === selectedCompanyId) {
    return token;
  }

  return applyCompanyContextToken(token, selectedCompanyId);
};

/**
 * POST /refresh with session cookie (withCredentials). Returns a new access token.
 * Concurrent callers share one in-flight refresh.
 * If a supervisor is inside a tenant board, re-issues the company context token.
 */
export const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const response = await refreshSessionRequest();
    const newToken = parseAccessTokenFromResponse(response.data);
    if (!newToken) {
      throw new Error('Refresh response missing access token');
    }

    if (getCompanyIdFromToken(newToken) === 0) {
      setPlatformAuthToken(newToken);
    }

    try {
      // Keep the current board token until the company context token is ready.
      // Publishing the supervisor token (companyId 0) here makes /drivers and
      // /drives return an empty board, and the map stays blank until the next poll.
      const contextToken = await ensureCompanyContextToken(newToken);
      setStoredAccessToken(contextToken);
      notifyAuthSession(contextToken);
      return contextToken;
    } catch {
      const hadCompanyContext = getSelectedCompanyId() != null;
      clearCompanySession();
      setStoredAccessToken(newToken);
      if (getCompanyIdFromToken(newToken) === 0) {
        setPlatformAuthToken(newToken);
      }
      notifyAuthSession(newToken);
      if (hadCompanyContext) {
        throw createCompanyContextLostError();
      }
      return newToken;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

/**
 * On startup: use stored access token, or try cookie-based refresh when missing.
 */
export const restoreAccessTokenFromSession = async () => {
  localStorage.removeItem('platformAuthToken');

  const existing = getStoredAccessToken();
  if (existing) {
    try {
      if (getSelectedCompanyId() != null && !getPlatformAuthToken()) {
        return await refreshAccessToken();
      }
      return await ensureCompanyContextToken(existing);
    } catch (error) {
      if (error?.code === COMPANY_CONTEXT_LOST) {
        return getStoredAccessToken();
      }
      try {
        return await refreshAccessToken();
      } catch (refreshError) {
        if (refreshError?.code === COMPANY_CONTEXT_LOST) {
          return getStoredAccessToken();
        }
        return existing;
      }
    }
  }

  try {
    return await refreshAccessToken();
  } catch (error) {
    if (error?.code === COMPANY_CONTEXT_LOST) {
      return getStoredAccessToken();
    }
    return null;
  }
};

export const forceLogoutRedirect = () => {
  clearStoredAccessToken();
  window.location.href = '/';
};
